// Rail.tsx — the persistent left rail (handoff README §1 "The shell" / Shell Anatomy.dc.html
// §01 "Vertical rhythm at 240px"). Hand-rolled `<aside>`, not shadcn's sidebar primitive —
// eight links and a badge don't earn that machinery (plan.md §Task 4). Only the nav scrolls
// (`flex: 1; overflow-y: auto`); nav → usage → feedback → account are all `shrink-0`, so the
// usage/upgrade block (Task 6, `UsageBlock.tsx`), the feedback trigger (THI-92,
// `FeedbackDialog.tsx`), and the account row can never be scrolled out of sight — the whole
// point of pinning them (README §1's vertical-rhythm table).
//
// Desktop-only (`hidden lg:flex`, breakpoint 1024px per the geometry table) — Task 5 adds the
// <1024px top bar + drawer. Below that width this task renders nothing in its place, which is
// the documented intermediate state, not a bug.
import { useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { BrandLogo } from '@repo/ui/components/brand-logo';
import { Skeleton } from '@repo/ui/components/skeleton';
import type { useSession } from '../../lib/auth-client';
import { useBots } from '../../hooks/useBots';
import { LAST_BOT_KEY } from '../../lib/landing';
import { useT } from '../../locales/context';
import { BotSwitcher } from './BotSwitcher';
import { FeedbackTrigger } from './FeedbackDialog';
import { RailNav } from './RailNav';
import { UsageBlock } from './UsageBlock';
import { WorkspaceMenuRow } from './WorkspaceMenu';

type Session = NonNullable<ReturnType<typeof useSession>['data']>;

// `--rail` (handoff README "one derived value is new and worth naming"): a Tailwind
// arbitrary value, not a new token — the rail's background is `--card` mixed toward
// `--background` so it reads as slightly separated from the content area rather than sharing
// it outright. Percentage differs 55%/62% between themes, so both branches are spelled out.
// Exported so Task 5's MobileTopBar and MobileDrawer share the exact same derived value
// instead of re-deriving it (both are `--rail` surfaces per handoff README §1's mobile table).
export const RAIL_BG =
  'bg-[color-mix(in_oklch,var(--card)_55%,var(--background))] dark:bg-[color-mix(in_oklch,var(--card)_62%,var(--background))]';
const RAIL_SHELL = `hidden w-60 shrink-0 flex-col border-r border-border px-3 pt-3.5 pb-3 lg:flex ${RAIL_BG}`;

/** One shape per real zone, at the real size (handoff README §6) — shown while the session
 * is still resolving (`Protected`'s `isPending`, the first thing every visitor sees) and
 * again here while `useBots` is still in flight, so nothing shifts when either lands. */
export function RailSkeleton() {
  return (
    <aside className={RAIL_SHELL} aria-hidden="true">
      <div className="flex h-[34px] shrink-0 items-center gap-2 px-1.5">
        <Skeleton className="size-[26px] rounded-lg" />
        <Skeleton className="h-[18px] w-20" />
      </div>
      <Skeleton className="mt-3.5 h-12 shrink-0 rounded-[10px]" />
      <div className="mt-[18px] flex flex-1 flex-col gap-2">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} className="h-9 rounded-lg" />
        ))}
      </div>
      {/* Usage block zone (96–128px, README §1 rhythm table) — reserved even here so nothing
          shifts vertically once `useBots` resolves and the real block mounts. */}
      <Skeleton className="mt-3.5 h-24 shrink-0 rounded-[10px]" />
      {/* Merged workspace+account row, 48px (2026-08-21 rail review). */}
      <Skeleton className="mt-2 h-12 shrink-0 rounded-lg" />
    </aside>
  );
}

/** Fix-wave item 1 — a settled-but-failed `useBots()` used to fall through to `RailSkeleton`
 * forever: `isPending` goes `false` on any settled query, success or failure, but `bots` stays
 * `undefined` on failure, and the old `if (isPending || !bots)` guard couldn't tell the two
 * apart. `main.tsx`'s bare `new QueryClient()` has no `throwOnError`/error boundary, so nothing
 * upstream catches this either — a `GET /api/bots` 500 permanently removed every navigation
 * affordance AND the account menu / sign-out from an `aria-hidden` rail, on every route, with no
 * way for a screen reader to even announce it. This renders instead: visible (no
 * `aria-hidden`), and — the part that matters most — keeps the workspace+account menu mounted
 * so sign-out always stays reachable, even though there is no bot list to build a switcher or
 * nav out of. */
function RailError({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  const t = useT();
  return (
    <aside className={RAIL_SHELL} role="alert">
      <Link to="/" className="flex h-[34px] shrink-0 items-center px-1.5">
        <BrandLogo size={26} />
      </Link>
      <div className="mt-[18px] flex flex-1 flex-col items-start gap-1 px-1.5">
        <p className="text-[13px] font-medium text-destructive">{t.shell.botsLoadError}</p>
        <p className="text-[12.5px] text-muted-foreground">{t.shell.reloadToRetry}</p>
      </div>
      <div className="mt-2 shrink-0">
        <WorkspaceMenuRow email={session.user.email} isAdmin={session.user.role === 'admin'} onSignOut={onSignOut} />
      </div>
    </aside>
  );
}

export function Rail({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  const { botId: routeBotId } = useParams();
  const { data: bots, isPending, error } = useBots();

  // Task 7: the single place that always mounts on a `/bots/:botId/*` route (Rail is a
  // sibling of `<Outlet>`, not a descendant, so it never unmounts on navigation within
  // `Protected`) — so this is where `embent:last-bot-id` gets written, rather than repeating
  // the effect in all eight bot pages. Guarded on `routeBotId` (not the switcher/nav's
  // first-bot fallback below): routes with no bot in the URL (`/bots`, `/account`, `/billing`,
  // `/admin/*`) must never overwrite a previously remembered bot.
  useEffect(() => {
    if (routeBotId) localStorage.setItem(LAST_BOT_KEY, routeBotId);
  }, [routeBotId]);

  if (isPending) return <RailSkeleton />;
  if (error || !bots) return <RailError session={session} onSignOut={onSignOut} />;

  // Most routes (Overview, Sources, ...) carry `:botId`; routes that don't (`/`, `/bots`,
  // `/account`, `/billing`, `/admin/*`) fall back to the first bot so the switcher and nav
  // always have a subject. Task 7 replaces this with the `embent:last-bot-id` resolver
  // (README §8) once `/` itself resolves to a bot instead of rendering All bots.
  const currentBotId = routeBotId ?? bots[0]?.id;

  return (
    <aside className={RAIL_SHELL}>
      <Link to="/" className="flex h-[34px] shrink-0 items-center px-1.5">
        <BrandLogo size={26} />
      </Link>

      <div className="mt-3.5 shrink-0">
        <BotSwitcher bots={bots} currentBotId={currentBotId} />
      </div>

      <nav className="mt-[18px] flex flex-1 flex-col overflow-y-auto">
        <RailNav botId={currentBotId} />
      </nav>

      {/* Feedback sits ABOVE the usage block (Johnny, 2026-08-18): the quota bar reads as one
          visual unit with the account row below it — wedging anything between them broke that
          adjacency. */}
      <div className="mt-3.5 shrink-0">
        <FeedbackTrigger />
      </div>

      <div className="mt-2 shrink-0">
        <UsageBlock />
      </div>

      <div className="mt-2 shrink-0">
        <WorkspaceMenuRow email={session.user.email} isAdmin={session.user.role === 'admin'} onSignOut={onSignOut} />
      </div>
    </aside>
  );
}
